import { useEffect, useState } from "react";
import { Loader2, Monitor, Smartphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classSlug: string;
  className: string;
  sessionLabel: string | null;
}

/** Renders the real parent email HTML (nothing is sent). */
export function WelcomePacketPreviewDialog({
  open,
  onOpenChange,
  classSlug,
  className,
  sessionLabel,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [sections, setSections] = useState<Array<{ key: string; heading: string }>>([]);
  const [details, setDetails] = useState<Record<string, string> | null>(null);
  const [status, setStatus] = useState<string>("draft");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setMessage(null);
    supabase.functions
      .invoke("welcome-packet-admin", {
        body: { action: "preview", classId: classSlug, className, sessionLabel },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setMessage(error.message || "Preview failed.");
          setHtml("");
          return;
        }
        setHtml(data?.html || "");
        setSubject(data?.subject || "");
        setSections(Array.isArray(data?.sections) ? data.sections : []);
        setDetails((data?.details as Record<string, string>) ?? null);
        setStatus(String(data?.status || "draft"));
        if (!data?.configured) setMessage(data?.message || "No packet configured yet.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, classSlug, className, sessionLabel]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Parent email preview</DialogTitle>
          <DialogDescription>
            Exactly what families receive for {className}
            {sessionLabel ? ` — ${sessionLabel}` : ""}. Nothing is sent from this screen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground truncate">
            {subject ? <>Subject: {subject}</> : null}
          </p>
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant={device === "desktop" ? "default" : "outline"}
              onClick={() => setDevice("desktop")}
            >
              <Monitor className="h-4 w-4 mr-1" /> Desktop
            </Button>
            <Button
              type="button"
              size="sm"
              variant={device === "mobile" ? "default" : "outline"}
              onClick={() => setDevice("mobile")}
            >
              <Smartphone className="h-4 w-4 mr-1" /> Mobile
            </Button>
          </div>
        </div>

        {message ? (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        ) : null}

        {status !== "ready" && !loading ? (
          <Alert>
            <AlertDescription>
              This packet is <strong>{status}</strong>, so families do not receive it yet. Mark it
              Ready in the Welcome Packet tab when the wording is final.
            </AlertDescription>
          </Alert>
        ) : null}

        {!loading && (sections.length > 0 || details) ? (
          <div className="grid gap-3 sm:grid-cols-2 text-xs">
            <div className="rounded-md border p-3">
              <p className="font-medium text-sm mb-1.5">Sections that will render</p>
              {sections.length ? (
                <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
                  {sections.map((s) => (
                    <li key={s.key}>{s.heading}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">None — nothing would be sent.</p>
              )}
            </div>
            <div className="rounded-md border p-3">
              <p className="font-medium text-sm mb-1.5">Verify the details</p>
              <dl className="space-y-0.5 text-muted-foreground">
                {[
                  ["Class", details?.className],
                  ["Session", details?.sessionLabel],
                  ["Dates", details?.meetingDates],
                  [
                    "Time",
                    [details?.startTime, details?.endTime].filter(Boolean).join(" – "),
                  ],
                  ["Instructor", details?.instructor],
                  [
                    "Location",
                    [details?.locationName, details?.streetAddress, details?.room]
                      .filter(Boolean)
                      .join(", "),
                  ],
                ].map(([label, value]) => (
                  <div key={String(label)} className="flex gap-2">
                    <dt className="min-w-[70px] shrink-0">{label}</dt>
                    <dd className="text-foreground">{value || "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        ) : null}

        <div className="flex justify-center bg-muted/40 rounded-md p-3 max-h-[65vh] overflow-auto">
          {loading ? (
            <div className="py-16 flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Rendering preview…
            </div>
          ) : html ? (
            <iframe
              title="Welcome packet preview"
              srcDoc={html}
              className="bg-background border rounded-md"
              style={{
                width: device === "desktop" ? 680 : 390,
                height: 800,
                border: "1px solid hsl(var(--border))",
              }}
            />
          ) : (
            <div className="py-16 text-muted-foreground text-sm">Nothing to preview yet.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WelcomePacketPreviewDialog;
